/**
 * API Client Implementation
 * 
 * This module provides a robust, type-safe API client implementation with built-in
 * validation, error handling, and React Query integration. It supports versioned
 * API endpoints and includes comprehensive request/response validation using Zod.
 * 
 * Features:
 * - Type-safe request/response handling
 * - Zod schema validation
 * - Versioned API support
 * - Standardized error handling
 * - React Query integration
 * - Request/response interceptors
 * - Automatic response parsing
 */

import { z } from 'zod';
import { ApiVersion, ApiError, ValidatedResponse } from './types';
import { QueryClient } from '@tanstack/react-query';

/**
 * Type-safe API client with built-in validation and React Query integration
 * 
 * @example
 * const client = new ApiClient({
 *   baseUrl: 'https://api.example.com',
 *   version: 'v1',
 *   queryClient: new QueryClient()
 * });
 * 
 * // Make a GET request
 * const response = await client.get('/users', userSchema);
 * 
 * // Make a POST request with validation
 * const result = await client.post('/users', userData, responseSchema, requestSchema);
 */
export class ApiClient {
	private baseUrl: string;
	private version: ApiVersion;
	private defaultHeaders: Record<string, string>;
	private queryClient: QueryClient;

	/**
	 * Creates a new API client instance
	 * @param {Object} config - Configuration options
	 * @param {string} config.baseUrl - Base URL for API requests
	 * @param {ApiVersion} config.version - API version to use
	 * @param {Record<string, string>} [config.defaultHeaders] - Default headers to include
	 * @param {QueryClient} config.queryClient - React Query client instance
	 */
	constructor(config: {
		baseUrl: string;
		version: ApiVersion;
		defaultHeaders?: Record<string, string>;
		queryClient: QueryClient;
	}) {
		this.baseUrl = config.baseUrl;
		this.version = config.version;
		this.queryClient = config.queryClient;
		this.defaultHeaders = {
			'Content-Type': 'application/json',
			Accept: `application/vnd.${config.version}+json`,
			...config.defaultHeaders,
		};
	}

	/**
	 * Validates API response against a Zod schema
	 * @template T Response data type
	 * @param {Response} response - Fetch Response object
	 * @param {z.ZodType<T>} schema - Zod schema for validation
	 * @returns {Promise<ValidatedResponse<T>>} Validated response data
	 * @private
	 */
	private async validateResponse<T>(
		response: Response,
		schema: z.ZodType<T>,
	): Promise<ValidatedResponse<T>> {
		if (!response.ok) {
			const error = await this.handleError(response);
			throw error;
		}

		const data = await response.json();
		const validatedData = schema.parse(data);

		return {
			statusCode: response.status,
			body: {
				data: validatedData,
				meta: {
					version: this.version,
					timestamp: new Date().toISOString(),
					requestId: response.headers.get('x-request-id') || crypto.randomUUID(),
				},
			},
			headers: Object.fromEntries(response.headers.entries()),
		};
	}

	/**
	 * Handles API error responses
	 * @param {Response} response - Error response
	 * @returns {Promise<ApiError>} Standardized error object
	 * @private
	 */
	private async handleError(response: Response): Promise<ApiError> {
		const errorData = await response.json().catch(() => ({}));

		switch (response.status) {
			case 400:
				return new ApiError(
					400,
					'VALIDATION_ERROR',
					errorData.message || 'Invalid request',
					errorData.details,
				);
			case 401:
				return new ApiError(401, 'AUTHENTICATION_ERROR', errorData.message || 'Unauthorized');
			case 403:
				return new ApiError(403, 'AUTHORIZATION_ERROR', errorData.message || 'Forbidden');
			case 404:
				return new ApiError(404, 'NOT_FOUND', errorData.message || 'Resource not found');
			case 429:
				return new ApiError(
					429,
					'RATE_LIMIT_EXCEEDED',
					errorData.message || 'Rate limit exceeded',
					{
						retryAfter: response.headers.get('Retry-After'),
					},
				);
			default:
				return new ApiError(
					response.status,
					'INTERNAL_ERROR',
					errorData.message || 'Internal server error',
				);
		}
	}

	/**
	 * Makes an HTTP request with validation
	 * @template T Response data type
	 * @param {string} endpoint - API endpoint
	 * @param {RequestInit} options - Fetch options
	 * @param {z.ZodType<T>} responseSchema - Response validation schema
	 * @returns {Promise<ValidatedResponse<T>>} Validated response
	 * @private
	 */
	private async makeRequest<T>(
		endpoint: string,
		options: RequestInit,
		responseSchema: z.ZodType<T>,
	): Promise<ValidatedResponse<T>> {
		const url = `${this.baseUrl}/${this.version}${endpoint}`;
		const headers = {
			...this.defaultHeaders,
			...options.headers,
		};

		try {
			const response = await fetch(url, {
				...options,
				headers,
			});

			return await this.validateResponse(response, responseSchema);
		} catch (error) {
			if (error instanceof ApiError) {
				throw error;
			}
			throw new ApiError(500, 'NETWORK_ERROR', 'Network request failed', error);
		}
	}

	/**
	 * Makes a GET request
	 * @template T Response data type
	 * @param {string} endpoint - API endpoint
	 * @param {z.ZodType<T>} responseSchema - Response validation schema
	 * @param {Record<string, string>} [params] - Query parameters
	 * @returns {Promise<ValidatedResponse<T>>} Validated response
	 * 
	 * @example
	 * const response = await client.get('/users', userSchema, { page: '1' });
	 */
	async get<T>(
		endpoint: string,
		responseSchema: z.ZodType<T>,
		params?: Record<string, string>,
	): Promise<ValidatedResponse<T>> {
		const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
		return this.makeRequest(`${endpoint}${queryString}`, { method: 'GET' }, responseSchema);
	}

	/**
	 * Makes a POST request
	 * @template T Request data type
	 * @template U Response data type
	 * @param {string} endpoint - API endpoint
	 * @param {T} data - Request data
	 * @param {z.ZodType<U>} responseSchema - Response validation schema
	 * @param {z.ZodType<T>} [requestSchema] - Request validation schema
	 * @returns {Promise<ValidatedResponse<U>>} Validated response
	 * 
	 * @example
	 * const response = await client.post('/users', userData, responseSchema, requestSchema);
	 */
	async post<T, U>(
		endpoint: string,
		data: T,
		responseSchema: z.ZodType<U>,
		requestSchema?: z.ZodType<T>,
	): Promise<ValidatedResponse<U>> {
		if (requestSchema) {
			requestSchema.parse(data);
		}

		return this.makeRequest(
			endpoint,
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			responseSchema,
		);
	}

	/**
	 * Makes a PUT request
	 * @template T Request data type
	 * @template U Response data type
	 * @param {string} endpoint - API endpoint
	 * @param {T} data - Request data
	 * @param {z.ZodType<U>} responseSchema - Response validation schema
	 * @param {z.ZodType<T>} [requestSchema] - Request validation schema
	 * @returns {Promise<ValidatedResponse<U>>} Validated response
	 * 
	 * @example
	 * const response = await client.put('/users/1', userData, responseSchema, requestSchema);
	 */
	async put<T, U>(
		endpoint: string,
		data: T,
		responseSchema: z.ZodType<U>,
		requestSchema?: z.ZodType<T>,
	): Promise<ValidatedResponse<U>> {
		if (requestSchema) {
			requestSchema.parse(data);
		}

		return this.makeRequest(
			endpoint,
			{
				method: 'PUT',
				body: JSON.stringify(data),
			},
			responseSchema,
		);
	}

	/**
	 * Makes a DELETE request
	 * @template T Response data type
	 * @param {string} endpoint - API endpoint
	 * @param {z.ZodType<T>} responseSchema - Response validation schema
	 * @returns {Promise<ValidatedResponse<T>>} Validated response
	 * 
	 * @example
	 * const response = await client.delete('/users/1', responseSchema);
	 */
	async delete<T>(endpoint: string, responseSchema: z.ZodType<T>): Promise<ValidatedResponse<T>> {
		return this.makeRequest(endpoint, { method: 'DELETE' }, responseSchema);
	}

	/**
	 * Makes a PATCH request
	 * @template T Request data type
	 * @template U Response data type
	 * @param {string} endpoint - API endpoint
	 * @param {T} data - Request data
	 * @param {z.ZodType<U>} responseSchema - Response validation schema
	 * @param {z.ZodType<T>} [requestSchema] - Request validation schema
	 * @returns {Promise<ValidatedResponse<U>>} Validated response
	 * 
	 * @example
	 * const response = await client.patch('/users/1', userData, responseSchema, requestSchema);
	 */
	async patch<T, U>(
		endpoint: string,
		data: T,
		responseSchema: z.ZodType<U>,
		requestSchema?: z.ZodType<T>,
	): Promise<ValidatedResponse<U>> {
		if (requestSchema) {
			requestSchema.parse(data);
		}

		return this.makeRequest(
			endpoint,
			{
				method: 'PATCH',
				body: JSON.stringify(data),
			},
			responseSchema,
		);
	}

	/**
	 * Creates a React Query query configuration
	 * @template T Query result type
	 * @param {string[]} key - Query key
	 * @param {() => Promise<T>} queryFn - Query function
	 * @returns {Object} Query configuration
	 * 
	 * @example
	 * const query = client.createQuery(['users'], () => client.get('/users', userSchema));
	 * const { data } = useQuery(query);
	 */
	createQuery<T>(key: string[], queryFn: () => Promise<T>) {
		return {
			queryKey: key,
			queryFn: async () => {
				try {
					const response = await queryFn();
					return response;
				} catch (error) {
					if (error instanceof ApiError) {
						throw error;
					}
					throw new ApiError(500, 'QUERY_ERROR', 'Query execution failed', error);
				}
			},
		};
	}

	/**
	 * Creates a React Query mutation configuration
	 * @template T Mutation variables type
	 * @template U Mutation result type
	 * @param {string[]} key - Mutation key
	 * @param {(data: T) => Promise<U>} mutationFn - Mutation function
	 * @returns {Object} Mutation configuration
	 * 
	 * @example
	 * const mutation = client.createMutation(['users'], 
	 *   (data) => client.post('/users', data, responseSchema)
	 * );
	 * const { mutate } = useMutation(mutation);
	 */
	createMutation<T, U>(key: string[], mutationFn: (data: T) => Promise<U>) {
		return {
			mutationKey: key,
			mutationFn: async (data: T) => {
				try {
					const response = await mutationFn(data);
					return response;
				} catch (error) {
					if (error instanceof ApiError) {
						throw error;
					}
					throw new ApiError(500, 'MUTATION_ERROR', 'Mutation execution failed', error);
				}
			},
		};
	}

	/**
	 * Invalidates React Query queries by key
	 * @param {string[]} key - Query key to invalidate
	 * @returns {Promise<void>} Promise that resolves when invalidation is complete
	 * 
	 * @example
	 * await client.invalidateQueries(['users']);
	 */
	invalidateQueries(key: string[]) {
		return this.queryClient.invalidateQueries({ queryKey: key });
	}
}
